import { JSX, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  BuildingOffice2Icon,
  EnvelopeIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { Avatar, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@heroui/react';

import { useAuth } from '../hooks/useAuth';
import { useInvitations } from '../hooks/useInvitations';
import { useLogout } from '../hooks/useLogout';
import { useTenant } from '../hooks/useTenant';
import { gravatarUrl } from '../utils/gravatar';

export function MenuDesktop(): JSX.Element {
  const { user } = useAuth();
  const { handleLogout } = useLogout();
  const { currentOrg, loading: tenantLoading } = useTenant();
  const { data: invitations } = useInvitations();
  const navigate = useNavigate();

  const invitationCount = invitations?.length || 0;

  const onLogout = useCallback((): void => {
    void handleLogout()
      .then(() => navigate('/login'))
      .catch(() => navigate('/login')); // Even if logout fails, redirect to login
  }, [handleLogout, navigate]);

  const handleAction = useCallback(
    (key: string | number): void => {
      if (key === 'profile') {
        void navigate('/settings/profile');
      } else if (key === 'invitations') {
        void navigate('/settings/invitations');
      } else if (key === 'org-settings') {
        void navigate('/org/settings');
      } else if (key === 'logout') {
        onLogout();
      }
    },
    [navigate, onLogout],
  );

  return (
    <Dropdown
      placement="bottom-end"
      classNames={{
        content: 'bg-surface-elevated',
      }}
    >
      <DropdownTrigger>
        <Avatar
          color="primary"
          name={user?.displayName || 'User'}
          size="sm"
          src={user?.photoURL ?? (user?.email ? gravatarUrl(user.email) : undefined)}
        />
      </DropdownTrigger>
      <DropdownMenu
        aria-label="Profile menu"
        onAction={handleAction}
        className="w-56"
        classNames={{
          base: 'bg-surface-elevated',
        }}
      >
        <DropdownItem
          key="user-info"
          isReadOnly
          className="h-14 gap-2 hover:bg-surface-elevated cursor-default"
        >
          <p className="text-sm font-medium">{user?.displayName || 'User'}</p>
          <p className="text-xs text-muted truncate">{user?.email}</p>
        </DropdownItem>
        <DropdownItem key="profile" startContent={<UserCircleIcon className="h-5 w-5" />}>
          Settings
        </DropdownItem>
        <DropdownItem
          key="invitations"
          startContent={<EnvelopeIcon className="h-5 w-5" />}
          endContent={
            invitationCount > 0 ? (
              <span className="bg-primary text-primary-foreground text-xs font-semibold px-2 py-0.5 rounded-full">
                {invitationCount}
              </span>
            ) : undefined
          }
        >
          Invitations
        </DropdownItem>
        <DropdownItem
          key="org-settings"
          className={currentOrg && !tenantLoading ? '' : 'hidden'}
          startContent={<BuildingOffice2Icon className="h-5 w-5" />}
        >
          Organization Settings
        </DropdownItem>
        <DropdownItem
          key="logout"
          className="text-danger-600 dark:text-danger-400"
          startContent={<ArrowRightOnRectangleIcon className="h-5 w-5" />}
        >
          Logout
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}
